import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import BugReportIcon from "@mui/icons-material/BugReport";
import BuildIcon from "@mui/icons-material/Build";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DataThresholdingIcon from "@mui/icons-material/DataThresholding";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EditIcon from "@mui/icons-material/Edit";
import EvStationIcon from "@mui/icons-material/EvStation";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HandymanIcon from "@mui/icons-material/Handyman";

import KeyIcon from "@mui/icons-material/Key";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import MapIcon from "@mui/icons-material/Map";
import MemoryIcon from "@mui/icons-material/Memory";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MonitorIcon from "@mui/icons-material/Monitor";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SensorsIcon from "@mui/icons-material/Sensors";
import SpeedIcon from "@mui/icons-material/Speed";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import WorkIcon from "@mui/icons-material/Work";
import { JSX } from "react";
import AdminPage from "@/pages/admin/AdminPage";
import AssetsPage from "@/pages/assets/AssetsPage";
import ClearancesPage from "@/pages/clearances/ClearancesPage";
import { DeviceFirmwarePageWrapper } from "@/pages/device-firmware/DeviceFirmwarePageWrapper";
import { DiagnosticEditDocs } from "@/pages/diagnostic-edit/components/DiagnosticEditDocs";
import DiagnosticEditPage from "@/pages/diagnostic-edit/DiagnosticEditPage";
import { DiagnosticMonitorPageWrapper } from "@/pages/diagnosticmonitor/components/DiagnosticMonitorPageWrapper";
import EnergyConsumptionReportPage from "@/pages/energy-consumption-report/EnergyConsumptionReportPage";
import { FaultOverviewPageWrapper } from "@/pages/faultmonitor/FaultOverviewPageWrapper";
import FleetDashboardPage from "@/pages/fleet-dashboard/FleetDashboardPage";
import { FuelCellPage } from "@/pages/fuelcell/FuelCellPage";
import GroupsPage from "@/pages/groups/GroupsPage";
import IbisIdPage from "@/pages/ibis-id/IbisIdPage";
import LicenseCheckAdminPage from "@/pages/license-check-admin/LicenseCheckAdminPage";
import LiveDataPage from "@/pages/live-data/LiveDataPage";
import LogbookAuditPage from "@/pages/logbook-audit/LogbookAuditPage";
import LogbookCheckPage from "@/pages/logbook-check/LogbookCheckPage";
import LogbookEditPage from "@/pages/logbook-edit/LogbookEditPage";
import { MaintenancePageWrapper } from "@/pages/maintenance/components/MaintenancePageWrapper";
import MapPage from "@/pages/map/MapPage";
import MotorDataPage from "@/pages/motor-data/MotorDataPage";
import PMRulesEditPage from "@/pages/pmrules-edit/PMRulesEditPage";
import { ManagePresets } from "@/pages/presets/ManagePresets";
import RoutePage from "@/pages/route/RoutePage";

import SpeedLimitReportPage from "@/pages/speed-limit-report/SpeedLimitReportPage";
import { TachographPage } from "@/pages/tachograph/TachographPage";
import UsersPage from "@/pages/users/UsersPage";
import VehicleDataCheckPage from "@/pages/vehicle-data-check/VehicleDataCheckPage";
import ZoneReportPage from "@/pages/zone-report/ZoneReportPage";
import ZonesPage from "@/pages/zones/ZonesPage";

export interface PageLink {
  name: string;
  groupName: string;
  icon: JSX.Element;
  component: JSX.Element;
  addInUrl: string;
  docs?: JSX.Element;
}
export const FAIRFLEET_SERVICES = "Fairfleet Services";
export const VEHICLE_STATUS = "VehicleStatus";
/** Add-in name of the admin page, which has no access group. */
export const ADMIN_ADDIN_NAME = "FairfleetAdmin";
/**
 * Geotab group names that gate access to each add-in page.
 *
 * New keys are named `Fairfleet<Page>` (e.g. `FairfleetLiveData`). The older keys without the
 * prefix stay as they are: renaming one revokes access in every customer database, because
 * memberships of the old group do not carry over. New groups are created through the admin
 * GroupBootstrapManager (licensing).
 */
export const enum GEOTAB_GROUP_NAMES {
  DIAGNOSEMONITOR = "Diagnosemonitor",
  STATUSMONITOR = "Statusmonitor",
  FAULTMONITOR = "Faultmonitor",
  DIAGNOSTICEDIT = "DiagnoseEdit",
  PMRULESEDIT = "PMRulesEdit",
  LICENSECHECKADMIN = "FairfleetLicenseCheckAdmin",
  DEVICEFIRMWARE = "FairfleetDeviceFirmware",
  LOGBOOKEDIT = "FairfleetLogbookEdit",
  LOGBOOKCHECK = "FairfleetLogbookCheck",
  PRESETS = "Presets",
  RULE_MANAGER = "RuleManager",
  MAINTENANCE = "Maintenance",
  MAP = "FairfleetLiveMap",
  /**
   * May set the group colours and symbols on the live map. They are shared, so changing them
   * changes the map for the whole company.
   */
  MAP_ADMIN = "FairfleetMapAdmin",
  ROUTE = "FairfleetRoute",
  IBISID = "FairfleetIbisId",
  LIVEDATA = "FairfleetLiveData",
  SPEED_LIMIT_REPORT = "FairfleetSpeedLimitReport",
  FUELCELL = "FairfleetH2Monitor",
  TACHOGRAPH = "FairfleetTachograph",
  LOGBOOK_AUDIT = "FairfleetLogbookAudit",
  USERS = "FairfleetUsers",
  ASSETS = "FairfleetAssets",
  FLEET_DASHBOARD = "FairfleetFleetDashboard",
  GROUPS = "FairfleetGroups",
  CLEARANCES = "FairfleetClearances",
  MOTOR_DATA = "FairfleetMotorData",
  VEHICLEDATACHECK = "FairfleetVehicleDataCheck",
  ENERGY_REPORT = "FairfleetEnergyReport",
  ZONES = "FairfleetZones",
  ZONE_REPORT = "FairfleetZoneReport",
}

export const groupPages = (): PageLink[] => [
  {
    name: "Diagnose Monitor",
    groupName: GEOTAB_GROUP_NAMES.DIAGNOSEMONITOR,
    icon: <MonitorIcon />,
    component: <DiagnosticMonitorPageWrapper pageType={"diagnosticMonitor"} />,
    addInUrl: "diagnosticmonitor.html",
  },
  {
    name: "Status Monitor",
    groupName: GEOTAB_GROUP_NAMES.STATUSMONITOR,
    icon: <MonitorIcon />,
    component: <DiagnosticMonitorPageWrapper pageType={"statusMonitor"} />,
    addInUrl: "statusmonitor.html",
  },
  {
    name: "Diagnose Setup",
    groupName: GEOTAB_GROUP_NAMES.DIAGNOSTICEDIT,
    icon: <EditIcon />,
    component: <DiagnosticEditPage />,
    addInUrl: "diagnostic-edit.html",
    docs: <DiagnosticEditDocs />,
  },
  {
    name: "PMRules Edit",
    groupName: GEOTAB_GROUP_NAMES.PMRULESEDIT,
    icon: <BuildIcon />,
    component: <PMRulesEditPage />,
    addInUrl: "pmrules-edit.html",
  },
  {
    name: "Fahrtenbuch",
    groupName: GEOTAB_GROUP_NAMES.LOGBOOKEDIT,
    icon: <MenuBookIcon />,
    component: <LogbookEditPage />,
    addInUrl: "logbook-edit.html",
  },
  {
    name: "Fahrtenbuch Prüfung",
    groupName: GEOTAB_GROUP_NAMES.LOGBOOKCHECK,
    icon: <MenuBookIcon />,
    component: <LogbookCheckPage />,
    addInUrl: "logbook-check.html",
  },
  {
    name: "Fehler Monitor",
    groupName: GEOTAB_GROUP_NAMES.FAULTMONITOR,
    icon: <BugReportIcon />,
    component: <FaultOverviewPageWrapper />,
    addInUrl: "faultmonitor.html",
  },
  {
    name: "Führerscheinüberprüfung",
    groupName: GEOTAB_GROUP_NAMES.LICENSECHECKADMIN,
    icon: <AdminPanelSettingsIcon />,
    component: <LicenseCheckAdminPage />,
    addInUrl: "license-check-admin.html",
  },
  {
    name: "Device Firmware",
    groupName: GEOTAB_GROUP_NAMES.DEVICEFIRMWARE,
    icon: <MemoryIcon />,
    component: <DeviceFirmwarePageWrapper />,
    addInUrl: "device-firmware.html",
  },
{
    name: "Presets",
    groupName: GEOTAB_GROUP_NAMES.PRESETS,
    icon: <HandymanIcon />,
    component: <ManagePresets />,
    addInUrl: "presets.html",
  },
  {
    name: "Auftragsverwaltung",
    groupName: GEOTAB_GROUP_NAMES.MAINTENANCE,
    icon: <WorkIcon />,
    component: <MaintenancePageWrapper />,
    addInUrl: "maintenance.html",
  },
  {
    name: "Karte",
    groupName: GEOTAB_GROUP_NAMES.MAP,
    icon: <MapIcon />,
    component: <MapPage />,
    addInUrl: "map.html",
  },
  {
    name: "Route",
    groupName: GEOTAB_GROUP_NAMES.ROUTE,
    icon: <AltRouteIcon />,
    component: <RoutePage />,
    addInUrl: "route.html",
  },
  {
    name: "IBIS ID",
    groupName: GEOTAB_GROUP_NAMES.IBISID,
    icon: <KeyIcon />,
    component: <IbisIdPage />,
    addInUrl: "ibis-id.html",
  },
  {
    name: "Live Data",
    groupName: GEOTAB_GROUP_NAMES.LIVEDATA,
    icon: <SensorsIcon />,
    component: <LiveDataPage />,
    addInUrl: "live-data.html",
  },
  {
    name: "Fahrzeugdaten-Check",
    groupName: GEOTAB_GROUP_NAMES.VEHICLEDATACHECK,
    icon: <PlaylistAddCheckIcon />,
    component: <VehicleDataCheckPage />,
    addInUrl: "vehicle-data-check.html",
  },
  {
    name: "Tempolimit-Meldungen",
    groupName: GEOTAB_GROUP_NAMES.SPEED_LIMIT_REPORT,
    icon: <SpeedIcon />,
    component: <SpeedLimitReportPage />,
    addInUrl: "speed-limit-report.html",
  },
  {
    name: "H2-Monitor",
    groupName: GEOTAB_GROUP_NAMES.FUELCELL,
    icon: <LocalGasStationIcon />,
    component: <FuelCellPage />,
    addInUrl: "fuelcell.html",
  },
  {
    name: "Tachograph",
    groupName: GEOTAB_GROUP_NAMES.TACHOGRAPH,
    icon: <TimerOutlinedIcon />,
    component: <TachographPage />,
    addInUrl: "tachograph.html",
  },
  {
    name: "Fahrtenbuch-Prüfung Zoll",
    groupName: GEOTAB_GROUP_NAMES.LOGBOOK_AUDIT,
    icon: <FactCheckIcon />,
    component: <LogbookAuditPage />,
    addInUrl: "logbook-audit.html",
  },
  {
    name: "Benutzer",
    groupName: GEOTAB_GROUP_NAMES.USERS,
    icon: <SupervisedUserCircleIcon />,
    component: <UsersPage />,
    addInUrl: "users.html",
  },
  {
    name: "Assets",
    groupName: GEOTAB_GROUP_NAMES.ASSETS,
    icon: <DirectionsCarIcon />,
    component: <AssetsPage />,
    addInUrl: "assets.html",
  },
  {
    name: "Flotten-Dashboard",
    groupName: GEOTAB_GROUP_NAMES.FLEET_DASHBOARD,
    icon: <DashboardIcon />,
    component: <FleetDashboardPage />,
    addInUrl: "fleet-dashboard.html",
  },
  {
    name: "Gruppen",
    groupName: GEOTAB_GROUP_NAMES.GROUPS,
    icon: <AccountTreeIcon />,
    component: <GroupsPage />,
    addInUrl: "groups.html",
  },
  {
    name: "Sicherheitsfreigaben",
    groupName: GEOTAB_GROUP_NAMES.CLEARANCES,
    icon: <AdminPanelSettingsIcon />,
    component: <ClearancesPage />,
    addInUrl: "clearances.html",
  },
  {
    name: "Motordaten",
    groupName: GEOTAB_GROUP_NAMES.MOTOR_DATA,
    icon: <DataThresholdingIcon />,
    component: <MotorDataPage />,
    addInUrl: "motor-data.html",
  },
  {
    name: "Energieverbrauch",
    groupName: GEOTAB_GROUP_NAMES.ENERGY_REPORT,
    icon: <EvStationIcon />,
    component: <EnergyConsumptionReportPage />,
    addInUrl: "energy-consumption-report.html",
  },
  {
    name: "Zonen",
    groupName: GEOTAB_GROUP_NAMES.ZONES,
    icon: <PentagonOutlinedIcon />,
    component: <ZonesPage />,
    addInUrl: "zones.html",
  },
  {
    name: "Zonenauswertung",
    groupName: GEOTAB_GROUP_NAMES.ZONE_REPORT,
    icon: <TimelapseIcon />,
    component: <ZoneReportPage />,
    addInUrl: "zone-report.html",
  },
  {
    name: "Admin",
    groupName: "",
    icon: <AdminPanelSettingsIcon />,
    component: <AdminPage />,
    addInUrl: "admin.html",
  },
];
